/**
 * OKR (Objectives and Key Results) Directory & Department Frameworks
 * Contains production-grade OKRs for major enterprise departments.
 */

export const OKR_DEPARTMENTS = [
  {
    "slug": "customer-success",
    "title": "Customer Success",
    "h1Title": "What is OKR for Customer Success?",
    "metaDescription": "Explore proven Customer Success OKRs, objectives, and measurable key results to increase retention, reduce churn, accelerate onboarding time, and elevate NPS.",
    "shortDesc": "Boost customer retention, maximize lifetime value (LTV), accelerate onboarding time, and achieve world-class Net Promoter Scores.",
    "pdfFileName": "OKR for Customer Success.pdf",
    "readTime": "3 min read",
    "publishedDate": "October 27, 2022",
    "lastModified": "September 03, 2025",
    "intro": "Customer success is the strategic engine that drives sustainable recurring revenue and brand advocacy. It increases revenue by raising customer retention and customer lifetime value (LTV). Setting ambitious, transparent OKRs enables your Customer Success Management (CSM) team to move from reactive firefighting to proactive customer lifecycle enablement, identifying risks before churn occurs.",
    "whatYouWillGet": [
      "Battle-tested OKR templates specifically tailored for B2B SaaS and Enterprise Customer Success teams.",
      "Clear quarterly time estimations and milestones required to achieve retention goals.",
      "Balanced quantitative targets (Gross Revenue Retention, Net Revenue Retention, CSAT, Time-to-First-Value).",
      "Proactive escalation frameworks to identify and rescue at-risk accounts before renewal.",
      "Standard operating procedures to increase CSM portfolio efficiency and account coverage.",
      "A systematic scoring methodology to track and measure weekly and monthly OKR confidence scores."
    ],
    "objectives": [
      {
        "id": "obj-1",
        "objective": "Accelerate Customer Onboarding & Shorten Time-to-First-Value (TTFV)",
        "quarter": "Q1 / Ongoing",
        "category": "Onboarding & Adoption",
        "keyResults": [
          {
            "kr": "Reduce average onboarding completion time from 45 days to 28 days.",
            "target": "28 days",
            "metric": "Days to Go-Live"
          },
          {
            "kr": "Achieve 85%+ product adoption score on core features within the first 30 days.",
            "target": "85%",
            "metric": "Feature Adoption"
          },
          {
            "kr": "Maintain a Customer Satisfaction (CSAT) score of 92% or higher across all onboarding kickoff milestones.",
            "target": "92%",
            "metric": "CSAT Score"
          }
        ]
      },
      {
        "id": "obj-2",
        "objective": "Maximize Net Revenue Retention (NRR) and Eliminate Controllable Churn",
        "quarter": "Q2 / Ongoing",
        "category": "Retention & Expansion",
        "keyResults": [
          {
            "kr": "Increase annual Gross Revenue Retention (GRR) from 89% to 95%.",
            "target": "95%",
            "metric": "GRR %"
          },
          {
            "kr": "Drive Net Revenue Retention (NRR) to 115% through proactive cross-selling and tier upgrades.",
            "target": "115%",
            "metric": "NRR %"
          },
          {
            "kr": "Conduct executive business reviews (EBRs) with 100% of top-tier tier-1 accounts 90 days before renewal.",
            "target": "100%",
            "metric": "EBR Coverage"
          }
        ]
      },
      {
        "id": "obj-3",
        "objective": "Cultivate High Brand Advocacy and World-Class Customer Health Scores",
        "quarter": "Q3 / Ongoing",
        "category": "Advocacy & Health",
        "keyResults": [
          {
            "kr": "Lift customer Net Promoter Score (NPS) from +42 to +65 across quarterly surveys.",
            "target": "+65",
            "metric": "NPS Score"
          },
          {
            "kr": "Secure 25 verified video testimonials and G2 customer review case studies.",
            "target": "25 Reviews",
            "metric": "Customer Advocacy"
          },
          {
            "kr": "Identify and resolve 95% of red health score accounts within 14 business days.",
            "target": "95%",
            "metric": "Health Recovery"
          }
        ]
      }
    ],
    "faqs": [
      {
        "q": "Why are OKRs critical for Customer Success teams?",
        "a": "OKRs align customer success reps with corporate revenue retention and expansion targets. Rather than just tracking ticket counts, OKRs keep the focus on strategic business outcomes like Time-to-Value, NRR, and customer lifecycle longevity."
      },
      {
        "q": "What is the difference between Customer Success OKRs and Support KPIs?",
        "a": "Customer Support KPIs generally focus on operational turnaround speed (First Response Time, Resolution Time, Ticket Backlog). Customer Success OKRs are outcome-driven strategic goals focused on product adoption, churn prevention, and account expansion."
      },
      {
        "q": "How frequently should Customer Success OKRs be reviewed?",
        "a": "While OKRs are typically planned on a quarterly basis, key results should be reviewed during weekly team check-ins to monitor confidence scores and unblock lagging adoption metrics."
      }
    ]
  },
  {
    "slug": "engineering-team",
    "title": "Engineering Team",
    "h1Title": "What is OKR for Engineering Team?",
    "metaDescription": "Practical Engineering OKRs for software development teams. Optimize deployment frequency, reduce bug escaped rates, improve uptime, and maintain high engineering velocity.",
    "shortDesc": "Enhance system reliability, scale microservices, shorten lead time for changes, and establish elite DevOps velocity.",
    "pdfFileName": "OKR for Engineering Team.pdf",
    "readTime": "3 min read",
    "publishedDate": "November 05, 2022",
    "lastModified": "September 03, 2025",
    "intro": "Engineering OKRs bridge technical architecture investments with business objectives. By defining clear, measurable Key Results around system uptime, lead time for changes, technical debt reduction, and deployment confidence, software engineering teams deliver robust product innovations while maintaining rock-solid infrastructure reliability.",
    "whatYouWillGet": [
      "Engineering OKRs focused on DORA metrics (Deployment Frequency, Lead Time, MTTR, Change Failure Rate).",
      "Strategies to balance technical debt remediation with product feature delivery.",
      "Infrastructure reliability and automated CI/CD pipeline optimization benchmarks.",
      "Security compliance and automated test coverage thresholds.",
      "Engineering velocity frameworks to measure sprint predictability without micromanagement."
    ],
    "objectives": [
      {
        "id": "eng-1",
        "objective": "Achieve Elite DevOps DORA Performance & Accelerate Release Cadence",
        "quarter": "Q1",
        "category": "DevOps & Velocity",
        "keyResults": [
          {
            "kr": "Increase deployment frequency from bi-weekly to daily on-demand automated deploys.",
            "target": "Daily",
            "metric": "Deployment Frequency"
          },
          {
            "kr": "Reduce Lead Time for Changes from 14 days to under 48 hours.",
            "target": "<48 hrs",
            "metric": "Lead Time"
          },
          {
            "kr": "Lower Change Failure Rate (CFR) on production releases from 7% to under 1.5%.",
            "target": "<1.5%",
            "metric": "CFR %"
          }
        ]
      },
      {
        "id": "eng-2",
        "objective": "Strengthen Infrastructure Resilience & System Availability",
        "quarter": "Q2",
        "category": "Reliability & Uptime",
        "keyResults": [
          {
            "kr": "Maintain 99.95% API uptime across all production regions (excluding planned maintenance).",
            "target": "99.95%",
            "metric": "Uptime"
          },
          {
            "kr": "Reduce Mean Time to Recovery (MTTR) during Sev-1/Sev-2 incidents to under 20 minutes.",
            "target": "<20 mins",
            "metric": "MTTR"
          },
          {
            "kr": "Automate failover testing and disaster recovery drills with zero data loss (RPO = 0).",
            "target": "100%",
            "metric": "DR Compliance"
          }
        ]
      },
      {
        "id": "eng-3",
        "objective": "Pay Down Technical Debt and Elevate Code Quality Standards",
        "quarter": "Q3",
        "category": "Quality & Security",
        "keyResults": [
          {
            "kr": "Increase unit and integration test code coverage from 68% to 85%+ across repositories.",
            "target": "85%",
            "metric": "Test Coverage"
          },
          {
            "kr": "Resolve 100% of high and critical security vulnerabilities within 7 days of detection.",
            "target": "100%",
            "metric": "Vulnerability SLA"
          },
          {
            "kr": "Deprecate legacy monolithic auth services in favor of OAuth2 microservice module.",
            "target": "100%",
            "metric": "Architecture Migration"
          }
        ]
      }
    ],
    "faqs": [
      {
        "q": "How can engineering teams set OKRs without stifling agility?",
        "a": "Focus engineering OKRs on business and architectural outcomes (e.g. system stability, build speeds, lead time) rather than specifying exact lines of code or micro-tasks."
      },
      {
        "q": "What are DORA metrics in engineering OKRs?",
        "a": "DORA metrics (Deployment Frequency, Lead Time for Changes, Mean Time to Recovery, and Change Failure Rate) are universally recognized benchmarks developed by Google Cloud to evaluate high-performing software teams."
      }
    ]
  },
  {
    "slug": "finance",
    "title": "Finance",
    "h1Title": "What is OKR for Finance?",
    "metaDescription": "Comprehensive Finance team OKRs covering budget predictability, accounts receivable DSO reduction, audit readiness, and cash runway management.",
    "shortDesc": "Streamline financial forecasting, accelerate monthly book closing, optimize working capital, and guarantee audit compliance.",
    "pdfFileName": "OKR for Finance.pdf",
    "readTime": "2 min read",
    "publishedDate": "November 12, 2022",
    "lastModified": "September 03, 2025",
    "intro": "Finance teams operate as strategic copilots for modern enterprises. Finance OKRs ensure disciplined capital allocation, precise financial visibility, accelerated monthly reconciliations, and strict regulatory adherence, ensuring the organization maintains healthy cash flow runway and investor confidence.",
    "whatYouWillGet": [
      "Operational OKRs to reduce Days Sales Outstanding (DSO) and accelerate collections.",
      "Monthly financial close benchmarks to compress closing timelines from weeks to days.",
      "Budget variance management templates to keep actual expenditures within forecasted limits.",
      "Audit preparation and statutory tax compliance protocols."
    ],
    "objectives": [
      {
        "id": "fin-1",
        "objective": "Accelerate Monthly Financial Close and Deliver Real-Time Executive Reporting",
        "quarter": "Q1",
        "category": "Financial Operations",
        "keyResults": [
          {
            "kr": "Reduce monthly financial closing timeline from 12 business days to 4 business days.",
            "target": "4 Days",
            "metric": "Close Speed"
          },
          {
            "kr": "Automate bank reconciliations and intercompany transfers for 95% of accounts.",
            "target": "95%",
            "metric": "Reconciliation Automation"
          },
          {
            "kr": "Publish monthly executive P&L and cash runway dashboards by the 5th of each month.",
            "target": "5th of Month",
            "metric": "Reporting SLA"
          }
        ]
      },
      {
        "id": "fin-2",
        "objective": "Optimize Cash Flow Management and Reduce Days Sales Outstanding (DSO)",
        "quarter": "Q2",
        "category": "Working Capital",
        "keyResults": [
          {
            "kr": "Decrease Days Sales Outstanding (DSO) from 58 days to 38 days.",
            "target": "38 Days",
            "metric": "DSO"
          },
          {
            "kr": "Recover 90% of overdue accounts receivable aging beyond 60 days.",
            "target": "90%",
            "metric": "AR Recovery"
          },
          {
            "kr": "Implement automated vendor invoice matching to capture 100% of early-payment discounts.",
            "target": "100%",
            "metric": "Vendor Discounts"
          }
        ]
      }
    ],
    "faqs": [
      {
        "q": "How does OKR implementation benefit corporate finance?",
        "a": "OKRs help finance teams transition from manual bookkeeping to proactive business strategy, optimizing working capital and mitigating financial risk."
      }
    ]
  },
  {
    "slug": "human-resources",
    "title": "Human Resources",
    "h1Title": "What is OKR for Human Resources?",
    "metaDescription": "HR OKR examples and goal frameworks for talent acquisition, employee engagement, performance appraisals, and employer branding.",
    "shortDesc": "Drive employee engagement, slash time-to-hire, retain top talent, and build an inclusive workplace culture.",
    "pdfFileName": "OKR for Human Resources.pdf",
    "readTime": "3 min read",
    "publishedDate": "November 20, 2022",
    "lastModified": "September 03, 2025",
    "intro": "People are the ultimate competitive advantage of every modern business. HR OKRs align talent acquisition, workplace culture, continuous learning, and employee retention with high-level corporate growth, creating an empowering environment where top performers thrive.",
    "whatYouWillGet": [
      "Talent acquisition OKRs to reduce Time-to-Hire and cost-per-hire.",
      "Employee engagement metrics to boost eNPS and lower voluntary attrition.",
      "Structured 30-60-90 day onboarding satisfaction targets.",
      "Learning & development frameworks to close organizational skill gaps."
    ],
    "objectives": [
      {
        "id": "hr-1",
        "objective": "Build a World-Class Talent Acquisition Engine to Support Business Expansion",
        "quarter": "Q1",
        "category": "Recruitment",
        "keyResults": [
          {
            "kr": "Reduce average Time-to-Hire from 52 days to 30 days across technical and sales roles.",
            "target": "30 Days",
            "metric": "Time-to-Hire"
          },
          {
            "kr": "Achieve an offer acceptance rate of 88% or higher by refining total reward transparency.",
            "target": "88%",
            "metric": "Offer Acceptance"
          },
          {
            "kr": "Source 40%+ of all qualified candidate hires through employee referral and inbound channels.",
            "target": "40%",
            "metric": "Referral Share"
          }
        ]
      },
      {
        "id": "hr-2",
        "objective": "Foster High Workplace Engagement & Slash Voluntary Employee Turnover",
        "quarter": "Q2",
        "category": "Culture & Retention",
        "keyResults": [
          {
            "kr": "Elevate Employee Net Promoter Score (eNPS) from +28 to +55 across biannual surveys.",
            "target": "+55",
            "metric": "eNPS Score"
          },
          {
            "kr": "Keep annualized voluntary employee turnover below 8% across all key business departments.",
            "target": "<8%",
            "metric": "Turnover Rate"
          },
          {
            "kr": "Implement 1-on-1 career growth blueprints for 100% of employees with quarterly reviews.",
            "target": "100%",
            "metric": "Career Pathing"
          }
        ]
      }
    ],
    "faqs": [
      {
        "q": "What are good Key Results for HR teams?",
        "a": "Strong HR Key Results are quantifiable outcomes: eNPS scores, time-to-hire days, offer acceptance percentages, and voluntary turnover rates."
      }
    ]
  },
  {
    "slug": "marketing",
    "title": "Marketing",
    "h1Title": "What is OKR for Marketing?",
    "metaDescription": "Proven Marketing OKRs for demand generation, organic SEO traffic, brand awareness, CAC optimization, and high-intent MQL pipeline.",
    "shortDesc": "Generate qualified inbound pipeline, dominate organic search rankings, optimize customer acquisition costs (CAC), and amplify brand reach.",
    "pdfFileName": "OKR for Marketing.pdf",
    "readTime": "3 min read",
    "publishedDate": "November 25, 2022",
    "lastModified": "September 03, 2025",
    "intro": "Modern marketing is a blend of creative storytelling and rigorous data science. Marketing OKRs provide clarity by aligning content creation, paid acquisition, product marketing, and brand messaging directly with sales pipeline generation and customer acquisition cost (CAC) efficiency.",
    "whatYouWillGet": [
      "Inbound demand generation targets tied directly to Marketing Qualified Leads (MQLs).",
      "Organic traffic, search ranking, and backlink authority growth blueprints.",
      "Paid advertising CAC reduction and conversion rate optimization (CRO) frameworks.",
      "Customer storytelling, event marketing, and product launch execution benchmarks."
    ],
    "objectives": [
      {
        "id": "mkt-1",
        "objective": "Supercharge Inbound Demand Generation & Deliver High-Intent Sales Pipeline",
        "quarter": "Q1",
        "category": "Demand Generation",
        "keyResults": [
          {
            "kr": "Generate 1,200 Marketing Qualified Leads (MQLs) resulting in ₹4.5 Cr in qualified sales pipeline.",
            "target": "1,200 MQLs",
            "metric": "Pipeline Value"
          },
          {
            "kr": "Increase organic search traffic by 60% through 25 strategic SEO long-tail topic clusters.",
            "target": "+60% Traffic",
            "metric": "SEO Growth"
          },
          {
            "kr": "Improve website landing page visitor-to-demo conversion rate from 2.4% to 4.2%.",
            "target": "4.2%",
            "metric": "Conversion Rate"
          }
        ]
      },
      {
        "id": "mkt-2",
        "objective": "Optimize Customer Acquisition Cost (CAC) and Maximize Paid ROI",
        "quarter": "Q2",
        "category": "Paid & Performance",
        "keyResults": [
          {
            "kr": "Reduce Blended Customer Acquisition Cost (CAC) by 25% across digital channels.",
            "target": "-25% CAC",
            "metric": "CAC Efficiency"
          },
          {
            "kr": "Achieve a minimum 4.5x Return on Ad Spend (ROAS) on search and social acquisition campaigns.",
            "target": "4.5x ROAS",
            "metric": "ROAS"
          },
          {
            "kr": "Launch 4 interactive product tour guides on homepage, driving 30% more product trial starts.",
            "target": "30% Lift",
            "metric": "Trial Starts"
          }
        ]
      }
    ],
    "faqs": [
      {
        "q": "How do marketing teams measure OKR success?",
        "a": "Marketing OKRs are measured by pipeline volume, cost per acquisition, organic traffic growth, and conversion rates."
      }
    ]
  },
  {
    "slug": "product-manager",
    "title": "Product Manager",
    "h1Title": "What is OKR for Product Manager?",
    "metaDescription": "Goal-setting framework for Product Managers. Define OKRs for feature adoption, user onboarding funnels, customer retention, and sprint roadmaps.",
    "shortDesc": "Deliver user-centric features, boost daily active engagement (DAU/MAU), and build intuitive product experiences that drive conversion.",
    "pdfFileName": "OKR for Product Manager.pdf",
    "readTime": "3 min read",
    "publishedDate": "December 01, 2022",
    "lastModified": "September 03, 2025",
    "intro": "Product Managers steer cross-functional squads to create products customers love. Product OKRs maintain focus on measurable customer behavior outcomes—such as user retention curves, activation funnels, and feature discovery—rather than just checking off feature output on a checklist.",
    "whatYouWillGet": [
      "User activation and onboarding funnel drop-off reduction benchmarks.",
      "Feature adoption and stickiness ratios (DAU/MAU) for product-led growth.",
      "Customer discovery interview cadences and validation loops.",
      "Product release velocity and post-launch customer delight metrics."
    ],
    "objectives": [
      {
        "id": "pm-1",
        "objective": "Streamline Product Activation and Create an Effortless Onboarding Experience",
        "quarter": "Q1",
        "category": "Activation & Onboarding",
        "keyResults": [
          {
            "kr": "Increase trial-to-paid conversion rate from 4.8% to 8.5%.",
            "target": "8.5%",
            "metric": "Conversion %"
          },
          {
            "kr": "Reduce self-serve setup completion time from 22 minutes to 8 minutes.",
            "target": "8 Mins",
            "metric": "Setup Time"
          },
          {
            "kr": "Achieve an 80% completion rate on the new interactive in-app welcome walkthrough.",
            "target": "80%",
            "metric": "Walkthrough Completion"
          }
        ]
      },
      {
        "id": "pm-2",
        "objective": "Deepen User Engagement and Elevate Core Feature Stickiness",
        "quarter": "Q2",
        "category": "Engagement & Stickiness",
        "keyResults": [
          {
            "kr": "Lift product stickiness ratio (DAU/MAU) from 32% to 48%.",
            "target": "48%",
            "metric": "DAU/MAU"
          },
          {
            "kr": "Conduct 40 in-depth qualitative customer discovery interviews to validate next-gen analytics suite.",
            "target": "40 Interviews",
            "metric": "Discovery Loops"
          },
          {
            "kr": "Achieve a Product-Market Fit (PMF) score of 55%+ on Sean Ellis survey ('very disappointed if product disappeared').",
            "target": "55%",
            "metric": "PMF Score"
          }
        ]
      }
    ],
    "faqs": [
      {
        "q": "What is the biggest mistake Product Managers make when setting OKRs?",
        "a": "Focusing on feature delivery ('Ship feature X by Friday') instead of measurable user outcomes ('Increase daily active usage by 20% via feature X')."
      }
    ]
  },
  {
    "slug": "quality-assurance",
    "title": "Quality Assurance",
    "h1Title": "What is OKR for Quality Assurance?",
    "metaDescription": "Actionable Quality Assurance (QA) OKRs for automation coverage, defect escape rate reduction, load testing, and release certification.",
    "shortDesc": "Automate regression suites, eliminate critical defect escapes, and accelerate release certification with zero regressions.",
    "pdfFileName": "OKR for Quality Assurance.pdf",
    "readTime": "3 min read",
    "publishedDate": "December 05, 2022",
    "lastModified": "September 03, 2025",
    "intro": "Quality Assurance is the guardian of product trust and user confidence. QA OKRs focus on transitioning from slow, repetitive manual test cycles to rapid automated regression testing, minimizing escaped defects in production, and verifying platform scalability under heavy traffic.",
    "whatYouWillGet": [
      "Automated testing coverage benchmarks across unit, integration, and E2E layers.",
      "Defect Escape Rate (DER) reduction frameworks.",
      "Test execution cycle time compression models for continuous deployment.",
      "Performance and load testing standards prior to peak traffic events."
    ],
    "objectives": [
      {
        "id": "qa-1",
        "objective": "Scale Automated End-to-End Test Coverage and Accelerate Release Sign-Off",
        "quarter": "Q1",
        "category": "Test Automation",
        "keyResults": [
          {
            "kr": "Increase automated regression test coverage for core business journeys from 40% to 85%.",
            "target": "85%",
            "metric": "Automation %"
          },
          {
            "kr": "Compress full regression test suite execution duration from 6 hours to under 35 minutes in CI.",
            "target": "<35 Mins",
            "metric": "Test Runtime"
          },
          {
            "kr": "Achieve zero false-positive flaky test failures across nightly automated CI test runs.",
            "target": "0 Flaky Tests",
            "metric": "Test Reliability"
          }
        ]
      },
      {
        "id": "qa-2",
        "objective": "Drive Down Production Defect Escapes and Protect Customer Experience",
        "quarter": "Q2",
        "category": "Defect Prevention",
        "keyResults": [
          {
            "kr": "Reduce Defect Escape Rate (DER) to production from 5.5% to under 1.0%.",
            "target": "<1.0%",
            "metric": "Defect Escape Rate"
          },
          {
            "kr": "Certify platform performance under 5x simulated peak concurrent traffic with zero service crashes.",
            "target": "5x Peak Load",
            "metric": "Load Testing"
          },
          {
            "kr": "Establish cross-browser and mobile device automated validation across 99% of user configurations.",
            "target": "99%",
            "metric": "Device Matrix"
          }
        ]
      }
    ],
    "faqs": [
      {
        "q": "What is Defect Escape Rate in QA OKRs?",
        "a": "Defect Escape Rate is the percentage of software bugs discovered by end-users in production versus those caught by QA during staging."
      }
    ]
  },
  {
    "slug": "sales-team",
    "title": "Sales Team",
    "h1Title": "What is OKR for Sales Team?",
    "metaDescription": "High-impact Sales Team OKRs for quarterly quota attainment, win rate optimization, outbound pipeline generation, and sales cycle compression.",
    "shortDesc": "Crush quarterly revenue quotas, expand deal sizes, shorten sales cycle duration, and accelerate outbound prospect conversion.",
    "pdfFileName": "OKR for Sales Team.pdf",
    "readTime": "3 min read",
    "publishedDate": "December 10, 2022",
    "lastModified": "September 03, 2025",
    "intro": "Sales teams power top-line corporate growth. Sales OKRs connect individual quota targets to strategic market penetration, win rate enhancements, outbound prospecting excellence, and average deal size expansion, establishing a predictable, scalable revenue machine.",
    "whatYouWillGet": [
      "Revenue quota attainment and ARR booking goals for Account Executives (AEs).",
      "Sales Qualified Opportunity (SQO) generation benchmarks for SDRs/BDRs.",
      "Sales cycle length reduction and discovery call-to-close conversion models.",
      "Enterprise up-market deal size expansion and contract value scaling."
    ],
    "objectives": [
      {
        "id": "sales-1",
        "objective": "Achieve Record Net-New ARR Bookings and Exceed Annual Sales Targets",
        "quarter": "Q1",
        "category": "Revenue & Quota",
        "keyResults": [
          {
            "kr": "Close ₹6.8 Cr in Net-New Annual Recurring Revenue (ARR) across Mid-Market & Enterprise sectors.",
            "target": "₹6.8 Cr",
            "metric": "New ARR"
          },
          {
            "kr": "Achieve 85%+ quota attainment across the full Account Executive team.",
            "target": "85%",
            "metric": "Team Attainment"
          },
          {
            "kr": "Increase Average Contract Value (ACV) by 30% through multi-product bundling.",
            "target": "+30% ACV",
            "metric": "Deal Size"
          }
        ]
      },
      {
        "id": "sales-2",
        "objective": "Shorten Sales Velocity and Maximize Opportunity Win Rates",
        "quarter": "Q2",
        "category": "Sales Velocity",
        "keyResults": [
          {
            "kr": "Lift demo-to-closed-won win rate from 19% to 28%.",
            "target": "28%",
            "metric": "Win Rate %"
          },
          {
            "kr": "Compress average sales cycle duration from 65 days to 42 days.",
            "target": "42 Days",
            "metric": "Sales Cycle"
          },
          {
            "kr": "Generate 250 qualified outbound sales opportunities through personalized multi-touch cadence.",
            "target": "250 Opps",
            "metric": "Outbound Opps"
          }
        ]
      }
    ],
    "faqs": [
      {
        "q": "Should sales quotas be separate from Sales OKRs?",
        "a": "Yes. Quotas are tactical individual commission benchmarks. Sales OKRs are strategic objectives (e.g. penetrating enterprise verticals, raising win rates) that enable reps to hit and exceed quotas."
      }
    ]
  },
  {
    "slug": "ui-ux-team",
    "title": "UI/UX Team",
    "h1Title": "What is OKR for UI/UX Team?",
    "metaDescription": "Design OKRs for product design, UI/UX research, design system adoption, usability testing scores, and accessibility compliance.",
    "shortDesc": "Craft delightful design experiences, unify design system components, enhance usability scores, and ensure WCAG accessibility.",
    "pdfFileName": "OKR for UI UX Team.pdf",
    "readTime": "3 min read",
    "publishedDate": "December 15, 2022",
    "lastModified": "September 03, 2025",
    "intro": "Exceptional UI/UX design turns complex business software into effortless, joyful experiences. Design OKRs measure usability satisfaction, design system efficiency, accessibility standards, and rapid prototype validation to ensure product interfaces solve real human problems seamlessly.",
    "whatYouWillGet": [
      "System Usability Scale (SUS) score benchmarks to measure user delight.",
      "Design system adoption and component tokenization frameworks.",
      "Usability testing cadences to eliminate workflow friction.",
      "WCAG 2.1 AA accessibility compliance and contrast validation standards."
    ],
    "objectives": [
      {
        "id": "ux-1",
        "objective": "Elevate Platform Usability and Deliver Intuitive User Workflows",
        "quarter": "Q1",
        "category": "Usability & Research",
        "keyResults": [
          {
            "kr": "Increase overall System Usability Scale (SUS) score from 68 to 85+ across user testing panels.",
            "target": "85+ SUS",
            "metric": "SUS Score"
          },
          {
            "kr": "Reduce task completion time for key user workflow (payroll processing) by 40%.",
            "target": "-40% Time",
            "metric": "Task Speed"
          },
          {
            "kr": "Conduct bi-weekly moderated usability tests with 30 active target users before final design handover.",
            "target": "30 Tests",
            "metric": "User Testing"
          }
        ]
      },
      {
        "id": "ux-2",
        "objective": "Scale Design System Maturity and Unify Cross-Platform Consistency",
        "quarter": "Q2",
        "category": "Design System",
        "keyResults": [
          {
            "kr": "Achieve 95% design system component coverage across all web and mobile interface screens.",
            "target": "95%",
            "metric": "Design Tokens"
          },
          {
            "kr": "Attain 100% WCAG 2.1 AA accessibility compliance across all high-traffic application pages.",
            "target": "100%",
            "metric": "Accessibility"
          },
          {
            "kr": "Reduce frontend design handoff revision cycles from 4 rounds to under 1.5 rounds.",
            "target": "<1.5 Rounds",
            "metric": "Handoff Speed"
          }
        ]
      }
    ],
    "faqs": [
      {
        "q": "How can design teams measure creative work with OKRs?",
        "a": "By tying design directly to user behavior and efficiency: System Usability Scale (SUS), task completion time, design system adoption, and accessibility scores."
      }
    ]
  }
];

export function getOkrBySlug(slug) {
  if (!slug) return null;
  const s = slug.toLowerCase().trim();
  return OKR_DEPARTMENTS.find(
    (item) => item.slug === s || item.slug === s.replace(/-okr$/, "") || item.title.toLowerCase() === s
  );
}
